import * as cnames from 'classnames'
import * as React from 'react'
import * as screenfull from 'screenfull'

import get from 'lodash-es/get'

import { BaseVideoPlayer, PlayState } from '@envato/react-video'

import EnhancedLazyLoad from '../../../components/enhancedLazyLoad'
import ClickOverlay from '../../video/clickOverlay'
import VideoControlsFullscreen from '../../video/videoControlsFullscreen'
import { IItemCard } from '../interface'
import LockupPreviewActionPanel from '../lockupPreviewActionPanel'

import * as styles from './lockupVideoPreview.module.css'

const firstVisibleVideosCount = 6

const { PLAY, PAUSE, RESET } = PlayState

interface IState {
  playState: PlayState
  wasTouchMoveDetected: boolean
  hideCursor: boolean
  videoPlayPromise: any
  videoLooped: boolean
  reportedEvents: string[]
  videoProgress: number
  seek?: number
  duration?: number
}

interface ILockupVideoPreview extends IItemCard {
  reportVideoProgress?: (eventName: string, videoSrc: string) => void
  reportVideoFullscreen?: (itemId: number) => void
  setUserVideoAutoPlayStatus?: (status: boolean) => void
  opaqueOnHoverClass: string
  muteVideo: () => any
  muted: boolean
}

class LockupVideoPreview extends React.Component<ILockupVideoPreview, IState> {
  private rootRef: React.RefObject<any>
  private playerRef: React.RefObject<HTMLInputElement>
  private videoResetTimeout: any
  private videoPlayTimeout: any
  private hideCursorTimeout: any
  private isFullscreen: boolean
  private wasFullscreen: boolean

  constructor(props: ILockupVideoPreview) {
    super(props)

    this.state = {
      playState: PAUSE,
      wasTouchMoveDetected: false,
      hideCursor: false,
      videoPlayPromise: null,
      videoLooped: false,
      reportedEvents: [],
      videoProgress: 0
    }

    this.rootRef = React.createRef()
    this.playerRef = React.createRef()
    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    this.startVideo = this.startVideo.bind(this)
    this.stopVideo = this.stopVideo.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onPlayError = this.onPlayError.bind(this)
    this.pauseVideo = this.pauseVideo.bind(this)
    this.handleSeek = this.handleSeek.bind(this)

    this.onVideoProgress = this.onVideoProgress.bind(this)
    this.reportProgressEvent = this.reportProgressEvent.bind(this)
    this.pauseIfVideoOffscreen = this.pauseIfVideoOffscreen.bind(this)
  }

  public componentWillUnmount() {
    this.discardVideoReset()
  }

  public render() {
    const {
      item,
      rank,
      onItemLinkClick,
      opaqueOnHoverClass,
      muted,
      muteVideo
    } = this.props

    const { playState } = this.state

    let videoLinkStyle
    let overlayStyle

    if (this.state.hideCursor) {
      videoLinkStyle = { cursor: 'none' }
      overlayStyle = { opacity: 0 }
    }

    const videoUrl = get(item, 'previews.icon_with_video_preview.video_url')
    const posterUrl = get(
      item,
      'previews.icon_with_video_preview.landscape_url'
    )

    const rootStyles = cnames(styles.root, {
      [styles.isFullscreen]: this.isFullscreen
    })

    this.isFullscreen =
      screenfull && screenfull.element === this.rootRef.current

    return (
      <div className={rootStyles} ref={this.rootRef}>
        <div
          role="button"
          className={styles.videoWrapper}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseOut}
          onMouseMove={this.onMouseMove}
          ref={this.playerRef}
        >
          <a
            className={cnames(styles.videoLink, 'video-link')}
            href={`${item.url}?s_rank=${rank}`}
            onClick={onItemLinkClick}
            style={videoLinkStyle}
            onTouchStart={this.onTouchStart}
            onTouchMove={this.onTouchMove}
            onTouchEnd={this.onTouchEnd}
            aria-label={item.name}
            role="button"
          >
            <EnhancedLazyLoad
              height="100%"
              once
              offset={200}
              disabled={rank <= firstVisibleVideosCount}
            >
              <React.Fragment>
                <BaseVideoPlayer
                  playState={playState}
                  muteVideo={muteVideo}
                  onVideoProgress={this.onVideoProgress}
                  muted={muted}
                  preload="none"
                  className={styles.video}
                  loop
                  playsInline
                  seek={this.state.seek}
                  src={videoUrl}
                  poster={posterUrl}
                  onPlayError={this.onPlayError}
                />
              </React.Fragment>
            </EnhancedLazyLoad>
          </a>

          {this.isFullscreen && (
            <ClickOverlay
              hideCursor={this.state.hideCursor}
              playState={playState}
              onPlayVideo={this.startVideo}
              onPauseVideo={this.pauseVideo}
            />
          )}

          <div
            role="progressbar"
            aria-valuenow={this.state.videoProgress}
            aria-valuemin={0}
            aria-valuemax={100}
            className={cnames(styles.progressBarWrapper, opaqueOnHoverClass)}
            onClick={this.handleSeek}
          >
            <div
              className={styles.progressBar}
              style={{
                width: this.state.videoProgress + '%'
              }}
            />
          </div>

          <section
            className={cnames(styles.hoverOverlay, opaqueOnHoverClass)}
            style={overlayStyle}
          >
            {this.renderVideoControls()}
          </section>
        </div>
      </div>
    )
  }

  private renderVideoControls = () => {
    if (this.isFullscreen) {
      return (
        <section className={styles.fullscreenControls}>
          <VideoControlsFullscreen
            playState={this.state.playState}
            onPlay={this.startVideo}
            onPause={this.pauseVideo}
            onExitFullscreen={this.handleFullScreen}
            onMouseMove={this.onMouseMove}
          />
        </section>
      )
    }

    return (
      <section className={styles.hoverOverlayActions}>
        <LockupPreviewActionPanel
          fullscreen
          onFullscreen={this.handleFullScreen}
          {...this.props}
        />
      </section>
    )
  }

  private handleFullScreen = () => {
    const { reportVideoFullscreen, item } = this.props

    if (screenfull && screenfull.enabled) {
      if (!screenfull.isFullscreen) {
        screenfull.on('change', this.handleFullScreenChange)

        if (reportVideoFullscreen) {
          reportVideoFullscreen(this.props.item.id)
        }
      }
      screenfull.toggle(this.rootRef.current)
    } else {
      const videoEl = this.rootRef.current.getElementsByTagName('video')[0]

      if (videoEl.webkitSupportsFullscreen) {
        this.startVideo()
        videoEl.webkitEnterFullscreen()
      }
    }
  }

  private handleFullScreenChange = () => {
    if (screenfull) {
      if (screenfull.isFullscreen) {
        this.startVideo()
        this.discardVideoReset()
        this.wasFullscreen = true
      } else {
        screenfull.off('change', this.handleFullScreenChange)
        this.stopVideo()
      }
    }
  }

  private onVideoProgress(
    playProgress: number,
    currentTime: number,
    duration: number
  ) {
    this.setState({
      duration,
      videoProgress: playProgress
    })

    this.reportProgressEvent(playProgress)
    this.pauseIfVideoOffscreen()
  }

  private reportProgressEvent(playProgress: number) {
    if (playProgress === 0) {
      this.reportEvent('video_start')
    }

    if (playProgress >= 25 && playProgress < 50) {
      this.reportEvent('25_percent')
    }

    if (playProgress >= 50 && playProgress < 75) {
      this.reportEvent('50_percent')
    }

    if (playProgress >= 75) {
      this.reportEvent('75_percent')
    }

    if (playProgress + 1 >= 100) {
      this.reportEvent('video_complete')
      this.setState({
        videoLooped: true
      })
    }
  }

  private handleSeek(event: any) {
    if (this.playerRef.current) {
      const progressbar = event.target.getBoundingClientRect()
      const player = this.playerRef.current.getBoundingClientRect()
      const delta = event.clientX - progressbar.left

      const progress = Math.round(delta * 100 / player.width)

      if (this.state.duration) {
        this.setState({
          videoProgress: progress,
          seek: this.state.duration * (progress / 100)
        })
      }
    }
  }

  private pauseIfVideoOffscreen() {
    if (this.state.playState === PLAY) {
      if (this.playerRef.current) {
        const pos = this.playerRef.current.getBoundingClientRect()

        if (document.documentElement) {
          const out: any = {
            top:
              pos.top >
              (window.innerHeight || document.documentElement.clientHeight),
            bottom: pos.bottom < 0
          }

          if (out.top || out.bottom) {
            this.stopVideo()
          }
        }
      }
    }
  }

  private hasEventReported(eventName: any) {
    return this.state.reportedEvents.includes(eventName)
  }

  private reportEvent(eventName: string) {
    if (!this.state.videoLooped && !this.hasEventReported(eventName)) {
      const { item } = this.props
      const videoSrc = get(item, 'previews.icon_with_video_preview.video_url')

      if (this.props.reportVideoProgress) {
        this.props.reportVideoProgress(eventName, videoSrc)
        this.setState({
          reportedEvents: [eventName, ...this.state.reportedEvents]
        })
      }
    }
  }

  private scheduleVideoReset() {
    this.videoResetTimeout = setTimeout(() => {
      this.setState({ playState: RESET, seek: undefined })

      // This fixes a rendering issue with chrome when exiting fullscreen.
      // Without this, when the video resets the video will dissapear and
      // you'll only see the black background of this `video-link`
      if (this.wasFullscreen) {
        const el = this.rootRef.current.getElementsByClassName('video-link')[0]
        this.wasFullscreen = false

        if (el) {
          el.style.display = 'flex'
          window.setTimeout(() => (el.style.display = ''))
        }
      }
    }, 5000)
  }

  private discardVideoReset() {
    if (this.videoResetTimeout) {
      clearTimeout(this.videoResetTimeout)
      this.videoResetTimeout = null
    }
  }

  private pauseVideo() {
    this.setState({ playState: PAUSE, hideCursor: false })
    clearTimeout(this.hideCursorTimeout)
  }

  private toggleVideo() {
    if (this.state.playState !== PLAY) {
      this.startVideo()
    } else {
      this.stopVideo()
    }
  }

  private scheduleVideoPlay() {
    this.discardScheduledVideoPlay()
    this.videoPlayTimeout = setTimeout(() => {
      this.setState({ playState: PLAY })
    }, 20)
  }

  private discardScheduledVideoPlay() {
    if (this.videoPlayTimeout) {
      clearTimeout(this.videoPlayTimeout)
      this.videoPlayTimeout = null
    }
  }

  private onTouchStart() {
    this.setState({
      wasTouchMoveDetected: false
    })
  }

  private onTouchMove() {
    this.setState({
      wasTouchMoveDetected: true
    })
  }

  private onTouchEnd(event: React.TouchEvent<HTMLAnchorElement>) {
    if (event.cancelable) {
      event.preventDefault()
    }

    if (!this.state.wasTouchMoveDetected) {
      this.toggleVideo()
    }
  }

  private startVideo() {
    this.hideCursorTimeout = setTimeout(() => {
      this.setState({ hideCursor: true })
    }, 3000)

    this.discardVideoReset()
    this.scheduleVideoPlay()
  }

  private stopVideo() {
    if (this.hideCursorTimeout) {
      clearTimeout(this.hideCursorTimeout)
    }

    this.setState({ hideCursor: false })

    this.discardScheduledVideoPlay()
    this.pauseVideo()
    this.scheduleVideoReset()
  }

  private onMouseMove() {
    if (this.state.hideCursor) {
      this.setState({ hideCursor: false })
    }

    clearTimeout(this.hideCursorTimeout)

    if (this.state.playState === PLAY) {
      this.hideCursorTimeout = setTimeout(() => {
        this.setState({ hideCursor: true })
      }, 3000)
    }
  }

  private onMouseOut = () => {
    if (!this.isFullscreen) {
      this.stopVideo()
    }
  }

  private onMouseEnter = () => {
    if (!this.isFullscreen) {
      this.startVideo()
    }
  }

  private onPlayError(error: any) {
    if (
      error.name === 'NotAllowedError' &&
      this.props.setUserVideoAutoPlayStatus
    ) {
      this.props.setUserVideoAutoPlayStatus(false)
    } else {
      throw error
    }
  }
}

export default LockupVideoPreview
